import { Filter, X, ChevronDown, ChevronUp, Search, User, Clock, DollarSign } from 'lucide-react';

export default function OrderFilters({
  filterStatus,
  setFilterStatus,
  filters,
  handleFilterChange,
  clearFilters,
  hasActiveFilters,
  showFilters,
  setShowFilters,
}) {
  const active = hasActiveFilters();
  const activeCount =
    Object.values(filters).filter((v) => v !== '').length + (filterStatus !== 'all' ? 1 : 0);

  return (
    <div className="bg-white rounded-xl border border-gray-200 mb-6" data-testid="orders-filters">
      <div className="p-4 flex items-center justify-between border-b border-gray-200">
        <div className="flex items-center gap-3">
          <Filter className="w-5 h-5" style={{ color: '#003594' }} />
          <h3 className="font-semibold" style={{ color: '#051C2C' }}>Filtros</h3>
          {active && (
            <span className="px-2 py-1 text-xs font-medium bg-blue-100 rounded-full" style={{ color: '#003594' }}>
              {activeCount} activos
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {active && (
            <button
              onClick={clearFilters}
              data-testid="clear-filters-btn"
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
              Limpiar
            </button>
          )}
          <button
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-btn"
            className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors"
            style={{ backgroundColor: '#E8F3FA', color: '#003594' }}
          >
            {showFilters ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Ocultar
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Mostrar
              </>
            )}
          </button>
        </div>
      </div>

      <div className="p-4 border-b border-gray-200">
        <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
        <div className="flex flex-wrap gap-2">
          {['all', 'pending', 'paid', 'expired', 'cancelled'].map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              data-testid={`status-filter-${status}`}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                filterStatus === status
                  ? 'text-white'
                  : 'text-gray-600 hover:bg-gray-100 border border-gray-300'
              }`}
              style={filterStatus === status ? { backgroundColor: '#003594' } : {}}
            >
              {status === 'all' ? 'Todas' : status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {showFilters && (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FilterField
              label={<><Search className="w-4 h-4 inline mr-1" />Referencia</>}
              type="text"
              value={filters.orderReference}
              onChange={(v) => handleFilterChange('orderReference', v)}
              placeholder="Buscar por referencia..."
            />
            <FilterField
              label={<><Search className="w-4 h-4 inline mr-1" />Código</>}
              type="text"
              value={filters.orderCode}
              onChange={(v) => handleFilterChange('orderCode', v)}
              placeholder="Buscar por código..."
            />
            <FilterField
              label={<><User className="w-4 h-4 inline mr-1" />Cliente</>}
              type="text"
              value={filters.payerSearch}
              onChange={(v) => handleFilterChange('payerSearch', v)}
              placeholder="Nombre o email..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FilterField
              label={<><Clock className="w-4 h-4 inline mr-1" />Desde</>}
              type="date"
              value={filters.fromDate}
              onChange={(v) => handleFilterChange('fromDate', v)}
            />
            <FilterField
              label={<><Clock className="w-4 h-4 inline mr-1" />Hasta</>}
              type="date"
              value={filters.toDate}
              onChange={(v) => handleFilterChange('toDate', v)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FilterField
              label={<><DollarSign className="w-4 h-4 inline mr-1" />Monto Mínimo</>}
              type="number"
              step="0.01"
              value={filters.minAmount}
              onChange={(v) => handleFilterChange('minAmount', v)}
              placeholder="0.00"
            />
            <FilterField
              label={<><DollarSign className="w-4 h-4 inline mr-1" />Monto Máximo</>}
              type="number"
              step="0.01"
              value={filters.maxAmount}
              onChange={(v) => handleFilterChange('maxAmount', v)}
              placeholder="0.00"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Método de Pago</label>
              <select
                value={filters.paymentMethod}
                onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Todos</option>
                <option value="p2p">P2P</option>
                <option value="immediate_debit">Débito Inmediato</option>
                <option value="bcv_transfer">Transferencia BCV</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Con Dispersión</label>
            <select
              value={filters.hasDispersion}
              onChange={(e) => handleFilterChange('hasDispersion', e.target.value)}
              className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todas</option>
              <option value="true">Con dispersión</option>
              <option value="false">Sin dispersión</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterField({ label, type, value, onChange, placeholder, step }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    </div>
  );
}
